import type { AggregationColor } from 'antd/es/color-picker/color';
import tinycolor from 'tinycolor2';

// Mid saturation/lightness keeps every generated marker legible on the map — no near-white
// or near-black picks, which fully random hex would produce.
const MARKER_SATURATION = 65;
const MARKER_LIGHTNESS = 45;

/**
 * Serialize a color picked in the AntD ColorPicker for persistence as the starter `markerColor`.
 * Opaque colors stay 6-digit (`#RRGGBB`) for back-compat; colors with transparency are emitted as
 * 8-digit hex (`#RRGGBBAA`) so the user-selected alpha survives the round-trip. tinycolor2 (1.6.0)
 * has no `toHex8String`, so the alpha byte is appended manually.
 */
export function markerColorToString(color: AggregationColor): string {
  const { r, g, b, a } = color.toRgb();
  const h = (n: number) => Math.round(n).toString(16).padStart(2, '0');
  const rgb = `#${h(r)}${h(g)}${h(b)}`;
  return a >= 1 ? rgb : `${rgb}${h(a * 255)}`;
}

/**
 * `count` visually distinct marker colors: hues evenly spaced around the color wheel from a
 * random offset, so no two are alike and the set differs on every run.
 */
export function getDistinctRandomColors(count: number): Array<string> {
  if (count <= 0) return [];

  const step = 360 / count;
  const offset = Math.random() * 360;

  const colors = Array.from({ length: count }, (_, i) =>
    tinycolor({
      h: (offset + i * step) % 360,
      s: MARKER_SATURATION / 100,
      l: MARKER_LIGHTNESS / 100,
    }).toHexString(),
  );

  // Shuffle, so neighbouring rows do not read as one smooth rainbow gradient.
  for (let i = colors.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [colors[i], colors[j]] = [colors[j], colors[i]];
  }

  return colors;
}
